// Originally from https://www.npmjs.com/package/use-custom-element
// Also took inspiration from https://www.npmjs.com/package/webcomponents-in-react
import React from 'react';

type FunctionsType = {
  key: string;
  fn: EventListener;
};

// Changes "onClick" to "click", etc.
const keyToEventName = (key: string): string => {
  return key.replace(/^on/, '').toLowerCase();
};

export type OverrideProps = {
  [propName: string]: string;
};

export const useCustomElement = (
  componentSelector: string,
  props: any,
  customMapping: OverrideProps = {}
) => {
  // Handle the change from className -> class automatically
  if (!Boolean(customMapping.className)) {
    customMapping.className = 'class';
  }

  const webComponentRef = React.useRef<HTMLElement | null>(null);

  React.useLayoutEffect(() => {
    const { current } = webComponentRef;

    let fns: Array<FunctionsType>;

    if (current) {
      // Handle all of the standard functions, set the properties on the ref
      Object.keys(props)
        // eslint-disable-next-line security/detect-object-injection
        .filter(
          (key) =>
            props.hasOwnProperty(key) && props[key] instanceof Function && !key.startsWith('on')
        )
        .forEach((key) => {
          // eslint-disable-next-line security/detect-object-injection
          (current as any)[customMapping[key] || key] = props[key];
        });

      // Handle all of the event listeners by adding the proper listener to the event
      fns = Object.keys(props)
        // eslint-disable-next-line security/detect-object-injection
        .filter((key) => props[key] instanceof Function && key.startsWith('on'))
        .map((key) => ({
          // eslint-disable-next-line security/detect-object-injection
          key: keyToEventName(customMapping[key] || key),
          // eslint-disable-next-line security/detect-object-injection
          fn: props[key],
        }));

      fns.forEach(({ key, fn }) => current.addEventListener(key, fn));
    }

    return () => {
      if (current) {
        fns.forEach(({ key, fn }) => current.removeEventListener(key, fn));
      }
    };
  }, [props, webComponentRef, customMapping]);

  // Handle the arrays and objects by converting them to a string
  const customElementProps = Object.keys(props)
    // eslint-disable-next-line security/detect-object-injection
    .filter((key) => !(props[key] instanceof Function))
    .reduce((acc, key) => {
      // eslint-disable-next-line security/detect-object-injection
      const prop = props[key];

      // eslint-disable-next-line security/detect-object-injection
      const computedKey = customMapping[key] || key;

      if (
        key !== 'children' &&
        key !== 'style' &&
        (prop instanceof Object || prop instanceof Array)
      ) {
        return { ...acc, [computedKey]: JSON.stringify(prop) };
      }

      return { ...acc, [computedKey]: prop };
    }, {});

  // Create the element, set the ref, apply the rest of the props
  return React.createElement(componentSelector, {
    ref: (ref: HTMLElement) => (webComponentRef.current = ref),
    ...customElementProps,
  });
};
